import { Injectable } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { createSelector, select, Store } from '@ngrx/store';
import { filter, map, take } from 'rxjs/operators';
import { SpecFieldNames } from '@ordersnproducts/modules/order-builder/modules/specification/constants/specification-field-names.enum';
import { OrderBuilderState } from '@ordersnproducts/modules/order-builder/reducers';
import { ConfigurableFormComponent } from '@ordersnproducts/modules/shared/modules/configurable-form/containers/configurable-form/configurable-form.component';
import { OnFieldChanged } from '@ordersnproducts/modules/shared/modules/configurable-form/models/on-field-changed';
import { LoadAddedValuesActionOP } from '@ordersnproducts/modules/order-builder/modules/rms-specification/actions/open-platform/added-values.actions';
import { generateAddedValueFormName } from '@ordersnproducts/modules/order-builder/modules/rms-specification/helpers/form-added-value-name-generator';
import { AddedValueFieldConfig } from '@ordersnproducts/modules/order-builder/modules/rms-specification/models/form-added-value-field-config';
import {
  getAvailableOpenPlatformAddedValues,
  getOpenPlatformProductAddedValues,
} from '@ordersnproducts/modules/order-builder/modules/rms-specification/selectors/added-value.selectors';
import {
  getRmsRecipeSpecifications,
  getRmsRehydrate,
} from '@ordersnproducts/modules/order-builder/modules/rms-specification/selectors/index';
import { RmsSpecificationRemoveRehydrateItem } from '@ordersnproducts/modules/order-builder/modules/rms-specification/actions/rms-specification.actions';
import { _safe } from '@ordersnproducts/modules/shared/utils/safe-get.method';
import { getOpenPlatformProducts } from '../../../../selectors/products.selectors';

const range = (n) => Array.from({ length: n }, (value, key) => key);

const rehydrateData = createSelector(
  getOpenPlatformProducts,
  getRmsRehydrate,
  getRmsRecipeSpecifications,
  getAvailableOpenPlatformAddedValues,
  (products, rehydrate, recipe, addedValues) => {
    return {
      products,
      rehydrate,
      recipe,
      addedValues,
    };
  },
);

const isRehydratation = (
  id: number,
  rehydrateState: {
    [id: number]: {
      [key: string]: any;
    };
  },
  config: AddedValueFieldConfig,
  fieldName = config.name,
) => {
  if (!!rehydrateState && rehydrateState[id] && config.rehydrate) {
    const rehydratationValue = _safe(() => rehydrateState[id][fieldName]);
    return !!rehydratationValue || fieldName in rehydrateState[id];
  } else {
    return false;
  }
};

/**
 * Loads the product added values
 * This only applies the first time added values loads (To the first value added dropdown)
 */
@Injectable()
export class LoadProductAddedValuesMixReferenceCommandService implements OnFieldChanged {
  constructor(private store: Store<OrderBuilderState>) {}
  onFieldChanged(
    value: string,
    formGroup: UntypedFormGroup,
    form: ConfigurableFormComponent,
    config: AddedValueFieldConfig,
  ): void {
    this.store
      .select(rehydrateData)
      .pipe(
        filter((data) => !!data.products && !!data.products.length),
        take(1),
      )
      .subscribe((data) => {
        const formId = _safe(() => formGroup.get(SpecFieldNames.ID).value);

        if (isRehydratation(form.data.id, data.rehydrate, config, generateAddedValueFormName(0))) {
          this.store.dispatch(new RmsSpecificationRemoveRehydrateItem({ id: form.data.id, key: config.name }));
        } else if (!!value) {
          this.store.dispatch(
            new LoadAddedValuesActionOP({
              productCode: value,
              index: 0,
              formId,
            }),
          );
        }
      });
  }
}

@Injectable()
export class LoadChildAddedValuesMixReferenceCommandService implements OnFieldChanged {
  constructor(private store: Store<OrderBuilderState>) {}

  private filterRehydrate(form: ConfigurableFormComponent, config: AddedValueFieldConfig) {
    return this.store.select(getRmsRehydrate).pipe(
      take(1),
      filter((rehydrateState) => {
        if (isRehydratation(form.data.id, rehydrateState, config)) {
          this.store.dispatch(new RmsSpecificationRemoveRehydrateItem({ id: form.data.id, key: config.name }));
          return false;
        } else {
          return true;
        }
      }),
    );
  }

  private accAddedValues(formGroup: UntypedFormGroup, config: AddedValueFieldConfig) {
    const keyName = `${'undefined'}_${formGroup.get(SpecFieldNames.ID).value}`;
    const tuples$ = this.store.pipe(
      select(getOpenPlatformProductAddedValues(keyName)),
      filter((tuples) => {
        return tuples && !!tuples.length && tuples[config.index] && tuples[config.index].addedValues !== undefined;
      }),
      take(1),
      map((tuples) => {
        // Accumulate added values
        return range(config.index + 1).map((_, index) => {
          const family = formGroup.get(generateAddedValueFormName(index)).value;
          if (tuples[index] && tuples[index].addedValues) {
            return tuples[index].addedValues.find((adddedValue) => adddedValue.valueAddedCode === family);
          } else {
            return null;
          }
        });
      }),
      filter((addedValue) => !!addedValue && !!addedValue.length),
    );
    return tuples$;
  }

  onFieldChanged(
    value: number,
    formGroup: UntypedFormGroup,
    form: ConfigurableFormComponent,
    config: AddedValueFieldConfig,
  ): void {
    this.filterRehydrate(form, config).subscribe(() => {
      // Guard
      const productCode = formGroup.get(SpecFieldNames.PRODUCT_SELECTION).value;
      const formId = formGroup.get(SpecFieldNames.ID).value;

      if (config.limit || !productCode) {
        return;
      }

      // Case when unselecting first added value
      if (!value && config.index === 0 && !!productCode) {
        this.store.dispatch(
          new LoadAddedValuesActionOP({
            productCode: productCode,
            index: 0,
            formId,
          }),
        );
        return;
      }

      // Get entire selected object
      this.accAddedValues(formGroup, config).subscribe((addedValues) => {
        // VALIDATES VALUES FOR CURRENT FIELD IN ORDER TO ASK FOR NEXT VAPS COMBINATION
        if (productCode && addedValues[config.index]) {
          // Unselect next one to avoid auto-selection when fetching finishes
          const control = formGroup.get(generateAddedValueFormName(config.index + 1));
          const userFormControl = formGroup.get(generateAddedValueFormName(config.index));
          if (userFormControl.dirty && control && control.value) {
            control.setValue(undefined);
          }
          const formIdValue = formGroup.get(SpecFieldNames.ID).value;
          this.store.dispatch(
            new LoadAddedValuesActionOP({
              productCode: productCode,
              index: config.index + 1,
              addedValues: addedValues,
              formId: formIdValue,
            }),
          );
        }
      });
    });
  }
}
